import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@matix/auth';
import { discountsAPI } from '@matix/api-client';
import type { DiscountReason } from '@matix/types';
import { roundMoney } from '@matix/utils';
import { amountStep, amountText, moneyPrecision } from '../../store/currency';
import { useCurrency } from '../print/useCurrency';
import { useLang } from '../../lib/language';
import { fmt } from '../../lib/mock-data';
import { XIcon, CheckIcon, TrashIcon, PercentIcon } from '../../components/icons';
import { OverrideModal } from '../modals/OverrideModal';
import type { CartLine, LineDiscount } from '../../store/cart';

/**
 * Discount ONE line.
 *
 * The sibling of `DiscountModal`, not a replacement: that one discounts the
 * whole receipt against `allow_overall_discount`, this one discounts a single
 * `Inv_Receipt_d` row against `ShowReceiptItemDiscount`. A cart can carry both,
 * and the server measures the cashier's limit against their SUM, which is why
 * the override obtained here is good for the order.
 *
 * Three rules worth stating, because each one exists to keep this modal and
 * the receipt telling the same story:
 *
 *   - **Amount is money off the WHOLE line**, not off one unit. That is what
 *     `Inv_Receipt_d.Discount_Value` means and what the server subtracts, and
 *     showing it any other way would make a 5-off on a qty-3 line read as 15.
 *   - **The discount can never exceed the line.** The input is capped at the
 *     line's own value rather than warning after the fact — the server would
 *     compute a negative line, and no receipt should carry one.
 *   - **The manager override is measured on THIS line's percent**, the figure
 *     the cashier is actually granting here. The server re-checks the combined
 *     total against the same limit, so an approval given for a big line
 *     discount also covers the header discount already in the cart.
 *
 * Layout follows `DiscountModal` element for element — the same toggle cards,
 * the same 56px value field with the unit inside it, the same preview box and
 * the same footer buttons — because the two are one feature to a cashier and
 * nothing here should look like a different app.
 */
export function LineDiscountModal({
  line,
  onSave,
  onClose,
}: {
  line: CartLine;
  /** Called with the new discount — null clears it. The modal closes itself. */
  onSave: (discount: LineDiscount | null) => void;
  onClose: () => void;
}) {
  const { t } = useLang();
  const { cashier } = useAuth();
  const currency = useCurrency();
  const precision = moneyPrecision();
  const rm = (n: number): number => roundMoney(n, precision);

  // A return line's discount by hand comes off what is left after the part of
  // the invoice discount it already carries (`refundUnitDiscount`).
  const lineValue = rm(
    line.unitPrice * line.qty - (line.isReturn ? Math.max(0, (line.refundUnitDiscount ?? 0) * line.qty) : 0),
  );

  // A fresh discount opens on AMOUNT (the user, 2026-09-30), like the order
  // discount; an existing one reopens on the type it was given.
  const [type, setType] = useState<'percent' | 'amount'>(line.lineDiscount?.type ?? 'amount');
  const [value, setValue] = useState<string>(
    line.lineDiscount ? String(line.lineDiscount.value) : '',
  );
  const [reasonId, setReasonId] = useState<number | null>(line.lineDiscount?.reasonId ?? null);
  const [override, setOverride] = useState<{ userId: number; username: string } | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);

  const { data: reasonsData } = useQuery({
    queryKey: ['discount-reasons'],
    queryFn: () => discountsAPI.listReasons(),
    staleTime: 10 * 60 * 1000,
  });
  const reasons: DiscountReason[] = reasonsData?.reasons ?? [];
  const selectedReason = reasons.find((r) => r.id === reasonId) ?? null;

  const numeric = Number(value);
  const entered = Number.isFinite(numeric) && numeric > 0 ? numeric : 0;

  // What actually comes off this line, capped at the line itself.
  const amount = useMemo(() => {
    const raw = type === 'percent' ? (lineValue * entered) / 100 : entered;
    return roundMoney(Math.min(Math.max(raw, 0), lineValue), precision);
  }, [type, entered, lineValue, precision]);

  // Always expressed as a percent for the permission check — that is the unit
  // Security_Users.MaxReceiptDiscountPerc is written in.
  const impliedPerc = lineValue > 0 ? (amount / lineValue) * 100 : 0;
  const cashierMaxPerc = cashier?.permissions?.max_receipt_discount_perc ?? 0;

  // A full comp is self-authorising, matching DiscountModal and the server's
  // own `isFullComp` — do not diverge from those two without changing all three.
  const isFullComp = impliedPerc >= 100;
  const needsOverride =
    !isFullComp &&
    amount > 0 &&
    (impliedPerc > cashierMaxPerc || selectedReason?.requires_manager_override === true);

  /*
   * A reason is NEVER mandatory — the same ops decision DiscountModal records
   * (`reasonRequired = false` there), and this modal had wrongly diverged from
   * it by blocking Apply until one was picked. The picker stays so a discount
   * CAN be documented, and a reason carrying `requires_manager_override` still
   * demands approval once chosen; it simply never gates Apply on its own.
   */
  const canApply = amount > 0 && (!needsOverride || override != null);

  function apply(): void {
    if (!canApply) return;
    onSave({
      type,
      value: entered,
      reasonId,
      reason: selectedReason
        ? t({ en: selectedReason.name, ar: selectedReason.name_ar || selectedReason.name })
        : t({ en: 'Discount', ar: 'خصم' }),
      ...(override ? { approvedBy: override.username, managerOverrideUserId: override.userId } : {}),
    });
    onClose();
  }

  function clear(): void {
    onSave(null);
    onClose();
  }

  return (
    <div className="modal-overlay show" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <div
            className="modal-header-img"
            style={{
              background: 'var(--primary-light, #e6f1fb)',
              color: 'var(--primary, #185fa5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PercentIcon size={20} />
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">
              {t({ en: 'Discount this item', ar: 'خصم على الصنف' })}
            </div>
            <div className="modal-header-name-ar">
              {line.product.name} · {fmt(lineValue)}
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label={t({ en: 'Close', ar: 'إغلاق' })}
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <button type="button" onClick={() => setType('percent')} style={toggleBtn(type === 'percent')}>
              <span>%</span>
              <span>{t({ en: 'Percent', ar: 'نسبة مئوية' })}</span>
            </button>
            <button type="button" onClick={() => setType('amount')} style={toggleBtn(type === 'amount')}>
              <span>{currency.code}</span>
              <span>{t({ en: 'Amount', ar: 'مبلغ' })}</span>
            </button>
          </div>

          <div>
            <label style={labelStyle}>{t({ en: 'Discount value', ar: 'قيمة الخصم' })}</label>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                inputMode="decimal"
                autoFocus
                min={0}
                max={type === 'percent' ? 100 : lineValue}
                step={type === 'percent' ? '0.01' : amountStep()}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') apply();
                }}
                placeholder={type === 'percent' ? '0' : amountText(0)}
                style={bigInputStyle}
              />
              <span style={inputUnitStyle}>{type === 'percent' ? '%' : currency.code}</span>
            </div>
          </div>

          {reasons.length > 0 && (
            <div>
              <label style={labelStyle}>
                {t({ en: 'Reason', ar: 'السبب' })}
                <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 11 }}>
                  {' '}· {t({ en: 'optional', ar: 'اختياري' })}
                </span>
              </label>
              <select
                style={selectStyle}
                value={reasonId ?? ''}
                onChange={(e) => {
                  setReasonId(e.target.value ? Number(e.target.value) : null);
                  // A new reason may carry its own override requirement, so an
                  // approval given for the previous one is no longer an
                  // approval for this one.
                  setOverride(null);
                }}
              >
                <option value="">{t({ en: 'No reason', ar: 'بدون سبب' })}</option>
                {reasons.map((r) => (
                  <option key={r.id} value={r.id}>
                    {t({ en: r.name, ar: r.name_ar || r.name })}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={previewBoxStyle}>
            <div style={previewRowStyle}>
              <span>{t({ en: 'Line value', ar: 'قيمة السطر' })}</span>
              <span>{fmt(lineValue)}</span>
            </div>
            <div style={{ ...previewRowStyle, color: 'var(--danger, #c0392b)' }}>
              <span>{t({ en: 'Discount', ar: 'الخصم' })}</span>
              <span>−{fmt(amount)}</span>
            </div>
            <div style={{ ...previewRowStyle, fontWeight: 700, fontSize: 15 }}>
              <span>{t({ en: 'Line total', ar: 'إجمالي السطر' })}</span>
              <span>{fmt(rm(lineValue - amount))}</span>
            </div>
          </div>

          {needsOverride && (
            <div style={overrideNoticeStyle}>
              {override
                ? t({
                    en: `Approved by ${override.username}`,
                    ar: `تمت الموافقة بواسطة ${override.username}`,
                  })
                : t({
                    en: `${impliedPerc.toFixed(0)}% is above your ${cashierMaxPerc}% limit — a manager must approve`,
                    ar: `${impliedPerc.toFixed(0)}% أعلى من حدك ${cashierMaxPerc}% — يلزم اعتماد مدير`,
                  })}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ gap: 8 }}>
          <div>
            {line.lineDiscount && (
              <button type="button" className="btn-secondary" onClick={clear}>
                <TrashIcon size={16} /> {t({ en: 'Remove', ar: 'إزالة' })}
              </button>
            )}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              {t({ en: 'Cancel', ar: 'إلغاء' })}
            </button>
            {needsOverride && !override ? (
              <button
                type="button"
                className="btn-primary-modal"
                onClick={() => setOverrideOpen(true)}
              >
                {t({ en: 'Manager approval', ar: 'اعتماد المدير' })}
              </button>
            ) : (
              <button
                type="button"
                className="btn-primary-modal"
                disabled={!canApply}
                onClick={apply}
                style={canApply ? undefined : { opacity: 0.4, cursor: 'not-allowed' }}
              >
                <CheckIcon size={16} /> {t({ en: 'Apply', ar: 'تطبيق' })}
              </button>
            )}
          </div>
        </div>
      </div>

      {overrideOpen && (
        <OverrideModal
          requestedDiscountPerc={impliedPerc}
          onSuccess={({ managerUserId, managerUsername }) => {
            setOverride({ userId: managerUserId, username: managerUsername });
            setOverrideOpen(false);
          }}
          onClose={() => setOverrideOpen(false)}
        />
      )}
    </div>
  );
}

/*
 * Inline, for the same reason DiscountModal's are: pos.css has no generic
 * `.input` or `.btn`. The modal vocabulary it DOES define is `.modal`,
 * `.modal-header`, `.modal-body`, `.modal-footer`, `.modal-actions`,
 * `.btn-secondary` and `.btn-primary-modal` — everything inside a modal body is
 * styled per modal. The first version of this file reached for `.input` and
 * `.btn`, which exist nowhere, so the popup rendered as unstyled browser
 * defaults. These values are the header discount modal's own.
 */
const labelStyle: import('react').CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-muted)',
  marginBottom: 6,
};

const bigInputStyle: import('react').CSSProperties = {
  width: '100%',
  height: 56,
  padding: '0 50px 0 16px',
  fontSize: 28,
  fontWeight: 600,
  textAlign: 'center',
  border: '1px solid var(--border-strong, #d0d6de)',
  borderRadius: 'var(--radius, 10px)',
  background: 'var(--surface, #fff)',
  color: 'var(--text)',
  outline: 'none',
};

const inputUnitStyle: import('react').CSSProperties = {
  position: 'absolute',
  right: 16,
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: 18,
  color: 'var(--text-muted)',
  pointerEvents: 'none',
};

const selectStyle: import('react').CSSProperties = {
  width: '100%',
  height: 40,
  padding: '0 12px',
  fontSize: 14,
  border: '1px solid var(--border, #e2e6ec)',
  borderRadius: 'var(--radius, 10px)',
  background: 'var(--surface, #fff)',
  color: 'var(--text)',
  outline: 'none',
};

const previewBoxStyle: import('react').CSSProperties = {
  background: 'var(--surface-2, #f3f5f8)',
  borderRadius: 'var(--radius, 10px)',
  padding: '12px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 13,
};

const previewRowStyle: import('react').CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
};

const overrideNoticeStyle: import('react').CSSProperties = {
  fontSize: 13,
  lineHeight: 1.4,
  color: 'var(--danger, #c0392b)',
  background: 'var(--danger-light, #fdecea)',
  border: '1px solid var(--danger, #c0392b)',
  borderRadius: 'var(--radius, 10px)',
  padding: '10px 12px',
};

function toggleBtn(active: boolean): import('react').CSSProperties {
  return {
    padding: '12px 10px',
    borderRadius: 'var(--radius, 10px)',
    border: active ? '2px solid var(--primary)' : '1px solid var(--border)',
    background: active ? 'var(--primary-light, #e6f1fb)' : 'var(--surface, #fff)',
    color: active ? 'var(--primary, #185fa5)' : 'var(--text-muted)',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    lineHeight: 1.2,
  };
}
